import React, { useState, useEffect, useCallback } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import apiService from '../services/apiService';
import { PERIOD_OPTIONS } from './PeriodSelector';
import './MPPTChart.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const MPPT_TABS = [
  { id: 'temperature', label: 'Temperature', unit: '°C' },
  { id: 'voltage', label: 'Voltage', unit: 'V' },
  { id: 'amps', label: 'Amps', unit: 'A' },
  { id: 'watts', label: 'Watts', unit: 'W' },
  { id: 'energy', label: 'Energy', unit: 'Wh' }
];

const MPPTChart = ({ selectedPeriod, selectedYear, selectedMonth, selected12Year, selected12Month }) => {
  const [data, setData] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTab, setSelectedTab] = useState('temperature');
  const [batteryGranularity, setBatteryGranularity] = useState('daily');
  const [batteryHourlyData, setBatteryHourlyData] = useState(null);

  const getDateRange = useCallback((period, year, month, year12, month12) => {
    const today = new Date();
    let startDate, endDate;
    
    if (period === 'select' && year && month) {
      startDate = new Date(year, month - 1, 1);
      endDate = new Date(year, month, 0);
    } else if (period === '12months' && year12 && month12) {
      startDate = new Date(year12, month12 - 1, 1);
      endDate = new Date(year12, month12 - 1 + 12, 0);
    } else if (period === 'current') {
      startDate = new Date(today.getFullYear(), today.getMonth(), 1);
      endDate = today;
    } else if (period === 'all') {
      startDate = new Date('2022-01-01');
      endDate = today;
    } else {
      const periodConfig = PERIOD_OPTIONS.find(p => p.id === period);
      startDate = new Date();
      startDate.setMonth(startDate.getMonth() - (periodConfig?.months || 1) + 1);
      startDate.setDate(1);
      endDate = today;
    }
    
    if (endDate > today) endDate = today;
    return { startDate, endDate };
  }, []);

  const loadData = useCallback(async (period, year, month, year12, month12) => {
    try {
      setLoading(true);
      setError(null);
      
      const periodConfig = PERIOD_OPTIONS.find(p => p.id === period);
      let response;
      if (period === 'select') {
        response = await apiService.getMpptData(null, year, month);
      } else if (period === '12months') {
        response = await apiService.getMpptData(12, year12, month12);
      } else {
        response = await apiService.getMpptData(periodConfig.months);
      }
      
      if (!response || !response.data || response.data.length === 0) {
        setError('No MPPT data available for this period');
        setData(null);
        setChartData(null);
        return;
      }

      setData(response);
    } catch (err) {
      console.error('Failed to load MPPT data:', err);
      setError(err.message || 'Failed to load MPPT data');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadBatteryHourlyData = useCallback(async (period, year, month, year12, month12) => {
    try {
      const { startDate, endDate } = getDateRange(period, year, month, year12, month12);
      const response = await apiService.getMpptBatteryHourly(
        startDate.toISOString(),
        endDate.toISOString()
      );
      setBatteryHourlyData(response);
    } catch (err) {
      console.error('Failed to load battery hourly data:', err);
      setBatteryHourlyData(null);
    }
  }, [getDateRange]);

  const buildChartData = useCallback((response, period, tab) => {
    if (!response?.data || response.data.length === 0) {
      setChartData(null);
      return;
    }
    
    const dataPoints = response.data.length;
    const useMonthYear = period === 'all' || period === '12months';
    
    const labels = response.data.map(d => {
      if (d.day) {
        const date = new Date(d.day);
        if (useMonthYear) {
          const month = date.toLocaleDateString('en-US', { month: 'short' });
          const year = date.getFullYear().toString().slice(-2);
          return `${month} ${year}`;
        } else {
          const month = date.toLocaleDateString('en-US', { month: 'short' });
          const day = date.getDate();
          return `${month} ${day}`;
        }
      }
      return d.label || 'N/A';
    });

    const getValue = (d, field) => {
      if (!d.hasData) return null;
      const val = parseFloat(d[field]);
      return isNaN(val) ? null : val;
    };

    let datasets = [];
    const pointRadius = dataPoints > 24 ? 2 : 4;

    if (tab === 'temperature') {
      datasets = [
        {
          label: 'Battery',
          data: response.data.map(d => getValue(d, 'avgBatteryTemp')),
          borderColor: 'rgb(34, 197, 94)',
          backgroundColor: 'rgba(34, 197, 94, 0.1)',
          borderWidth: 2,
          pointRadius,
          pointHoverRadius: 6,
          tension: 0.3,
          spanGaps: false
        },
        {
          label: 'MPPT',
          data: response.data.map(d => getValue(d, 'avgMpptTemp')),
          borderColor: 'rgb(239, 68, 68)',
          backgroundColor: 'rgba(239, 68, 68, 0.1)',
          borderWidth: 2,
          pointRadius,
          pointHoverRadius: 6,
          tension: 0.3,
          spanGaps: false
        }
      ];
    } else if (tab === 'voltage') {
      datasets = [
        {
          label: 'Battery',
          data: response.data.map(d => getValue(d, 'avgBatteryVoltage')),
          borderColor: 'rgb(34, 197, 94)',
          backgroundColor: 'rgba(34, 197, 94, 0.1)',
          borderWidth: 2,
          pointRadius,
          pointHoverRadius: 6,
          tension: 0.3,
          spanGaps: false
        },
        {
          label: 'Load',
          data: response.data.map(d => getValue(d, 'avgLoadVoltage')),
          borderColor: 'rgb(59, 130, 246)',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          borderWidth: 2,
          pointRadius,
          pointHoverRadius: 6,
          tension: 0.3,
          spanGaps: false
        },
        {
          label: 'PV',
          data: response.data.map(d => getValue(d, 'avgPvVoltage')),
          borderColor: 'rgb(251, 191, 36)',
          backgroundColor: 'rgba(251, 191, 36, 0.1)',
          borderWidth: 2,
          pointRadius,
          pointHoverRadius: 6,
          tension: 0.3,
          spanGaps: false
        }
      ];
    } else if (tab === 'amps') {
      datasets = [
        {
          label: 'Battery',
          data: response.data.map(d => getValue(d, 'avgBatteryCurrent')),
          borderColor: 'rgb(34, 197, 94)',
          backgroundColor: 'rgba(34, 197, 94, 0.1)',
          borderWidth: 2,
          pointRadius,
          pointHoverRadius: 6,
          tension: 0.3,
          spanGaps: false
        },
        {
          label: 'Load',
          data: response.data.map(d => getValue(d, 'avgLoadCurrent')),
          borderColor: 'rgb(59, 130, 246)',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          borderWidth: 2,
          pointRadius,
          pointHoverRadius: 6,
          tension: 0.3,
          spanGaps: false
        },
        {
          label: 'PV',
          data: response.data.map(d => getValue(d, 'avgPvCurrent')),
          borderColor: 'rgb(251, 191, 36)',
          backgroundColor: 'rgba(251, 191, 36, 0.1)',
          borderWidth: 2,
          pointRadius,
          pointHoverRadius: 6,
          tension: 0.3,
          spanGaps: false
        }
      ];
    } else if (tab === 'watts') {
      datasets = [
        {
          label: 'Load',
          data: response.data.map(d => getValue(d, 'avgLoadPower')),
          borderColor: 'rgb(59, 130, 246)',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          borderWidth: 2,
          pointRadius,
          pointHoverRadius: 6,
          tension: 0.3,
          spanGaps: false
        },
        {
          label: 'PV',
          data: response.data.map(d => getValue(d, 'avgPvPower')),
          borderColor: 'rgb(251, 191, 36)',
          backgroundColor: 'rgba(251, 191, 36, 0.1)',
          borderWidth: 2,
          pointRadius,
          pointHoverRadius: 6,
          tension: 0.3,
          spanGaps: false
        },
        {
          label: 'Max Charge Today',
          data: response.data.map(d => getValue(d, 'maxChargeToday')),
          borderColor: 'rgb(34, 197, 94)',
          backgroundColor: 'rgba(34, 197, 94, 0.1)',
          borderWidth: 2,
          pointRadius,
          pointHoverRadius: 6,
          tension: 0.3,
          spanGaps: false
        },
        {
          label: 'Max Discharge Today',
          data: response.data.map(d => getValue(d, 'maxDischargeToday')),
          borderColor: 'rgb(239, 68, 68)',
          backgroundColor: 'rgba(239, 68, 68, 0.1)',
          borderWidth: 2,
          pointRadius,
          pointHoverRadius: 6,
          tension: 0.3,
          spanGaps: false
        }
      ];
    } else if (tab === 'energy') {
      datasets = [
        {
          label: 'Generated',
          data: response.data.map(d => getValue(d, 'wattsGeneratedToday')),
          borderColor: 'rgb(34, 197, 94)',
          backgroundColor: 'rgba(34, 197, 94, 0.1)',
          borderWidth: 2,
          pointRadius,
          pointHoverRadius: 6,
          tension: 0.3,
          spanGaps: false
        },
        {
          label: 'Consumed',
          data: response.data.map(d => getValue(d, 'wattsConsumedToday')),
          borderColor: 'rgb(239, 68, 68)',
          backgroundColor: 'rgba(239, 68, 68, 0.1)',
          borderWidth: 2,
          pointRadius,
          pointHoverRadius: 6,
          tension: 0.3,
          spanGaps: false
        }
      ];
    } else if (tab === 'battery') {
      datasets = [
        {
          label: 'Battery Charge',
          data: response.data.map(d => getValue(d, 'avgBatteryPercent')),
          borderColor: 'rgb(34, 197, 94)',
          backgroundColor: 'rgba(34, 197, 94, 0.1)',
          borderWidth: 2,
          pointRadius,
          pointHoverRadius: 6,
          tension: 0.3,
          spanGaps: false,
          yAxisID: 'y'
        },
        {
          label: 'Outside Temp',
          data: response.data.map(d => getValue(d, 'outsideTemp')),
          borderColor: 'rgb(251, 191, 36)',
          backgroundColor: 'rgba(251, 191, 36, 0.1)',
          borderWidth: 2,
          pointRadius,
          pointHoverRadius: 6,
          tension: 0.3,
          spanGaps: false,
          yAxisID: 'y1'
        }
      ];
    }

    setChartData({ labels, datasets });
  }, []);

  const buildBatteryHourlyChartData = useCallback((hourlyData) => {
    if (!hourlyData?.data || hourlyData.data.length === 0) {
      setChartData(null);
      return;
    }
    
    const dataPoints = hourlyData.data.length;
    const pointRadius = dataPoints > 100 ? 1 : dataPoints > 50 ? 2 : 3;
    
    const labels = hourlyData.data.map(d => {
      const date = new Date(d.recordedAt);
      const month = date.toLocaleDateString('en-US', { month: 'short' });
      const day = date.getDate();
      const hour = date.getHours().toString().padStart(2, '0');
      return `${month} ${day} ${hour}:00`;
    });

    const datasets = [
      {
        label: 'Battery Charge',
        data: hourlyData.data.map(d => d.batteryPercent !== null ? d.batteryPercent : null),
        borderColor: 'rgb(34, 197, 94)',
        backgroundColor: 'rgba(34, 197, 94, 0.1)',
        borderWidth: 2,
        pointRadius,
        pointHoverRadius: 6,
        tension: 0.3,
        spanGaps: false,
        yAxisID: 'y'
      },
      {
        label: 'Outside Temp',
        data: hourlyData.data.map(d => d.outsideTemp !== null ? parseFloat(d.outsideTemp) : null),
        borderColor: 'rgb(251, 191, 36)',
        backgroundColor: 'rgba(251, 191, 36, 0.1)',
        borderWidth: 2,
        pointRadius,
        pointHoverRadius: 6,
        tension: 0.3,
        spanGaps: false,
        yAxisID: 'y1'
      }
    ];

    setChartData({ labels, datasets });
  }, []);

  useEffect(() => {
    loadData(selectedPeriod, selectedYear, selectedMonth, selected12Year, selected12Month);
  }, [selectedPeriod, selectedYear, selectedMonth, selected12Year, selected12Month, loadData]);

  useEffect(() => {
    if (selectedTab === 'battery' && batteryGranularity === 'hourly') {
      loadBatteryHourlyData(selectedPeriod, selectedYear, selectedMonth, selected12Year, selected12Month);
    }
  }, [selectedTab, batteryGranularity, selectedPeriod, selectedYear, selectedMonth, selected12Year, selected12Month, loadBatteryHourlyData]);

  useEffect(() => {
    if (selectedTab === 'battery' && batteryGranularity === 'hourly') {
      if (batteryHourlyData) {
        buildBatteryHourlyChartData(batteryHourlyData);
      }
    } else if (data) {
      buildChartData(data, selectedPeriod, selectedTab);
    }
  }, [data, selectedPeriod, selectedTab, batteryGranularity, batteryHourlyData, buildChartData, buildBatteryHourlyChartData]);

  const getOptions = () => {
    const tabConfig = MPPT_TABS.find(t => t.id === selectedTab);
    const unit = tabConfig?.unit || '';
    const isBatteryTab = selectedTab === 'battery';
    
    return {
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        mode: 'index',
        intersect: false,
      },
      plugins: {
        legend: {
          position: 'top',
          labels: {
            color: 'rgba(255, 255, 255, 0.8)',
            usePointStyle: true,
            padding: 20
          }
        },
        tooltip: {
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          titleColor: 'white',
          bodyColor: 'white',
          borderColor: 'rgba(255, 255, 255, 0.2)',
          borderWidth: 1,
          callbacks: {
            label: function(context) {
              const datasetUnit = context.dataset.yAxisID === 'y1' ? '°C' : unit;
              return `${context.dataset.label}: ${context.parsed.y}${datasetUnit}`;
            }
          }
        }
      },
      scales: {
        x: {
          ticks: {
            color: 'rgba(255, 255, 255, 0.6)',
            maxRotation: 45,
            minRotation: 0,
            autoSkip: true,
            maxTicksLimit: 12
          },
          grid: {
            color: 'rgba(255, 255, 255, 0.1)'
          }
        },
        y: {
          type: 'linear',
          display: true,
          position: 'left',
          ticks: {
            color: 'rgba(255, 255, 255, 0.6)',
            callback: function(value) {
              return value + unit;
            }
          },
          grid: {
            color: 'rgba(255, 255, 255, 0.1)'
          }
        },
        ...(isBatteryTab && {
          y1: {
            type: 'linear',
            display: true,
            position: 'right',
            title: {
              display: true,
              text: 'Temperature (°C)',
              color: 'rgb(251, 191, 36)'
            },
            ticks: {
              color: 'rgb(251, 191, 36)',
              callback: function(value) {
                return value + '°C';
              }
            },
            grid: {
              drawOnChartArea: false
            }
          }
        })
      }
    };
  };

  return (
    <div className="mppt-chart">
      <div className="chart-header">
        <h3>MPPT</h3>
        <div className="mppt-tabs">
          {MPPT_TABS.map(tab => (
            <button
              key={tab.id}
              className={`mppt-tab ${selectedTab === tab.id ? 'active' : ''}`}
              onClick={() => setSelectedTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>
      
      {selectedTab === 'battery' && (
        <div className="battery-granularity-toggle">
          <button
            className={`granularity-btn ${batteryGranularity === 'daily' ? 'active' : ''}`}
            onClick={() => setBatteryGranularity('daily')}
          >
            Daily
          </button>
          <button
            className={`granularity-btn ${batteryGranularity === 'hourly' ? 'active' : ''}`}
            onClick={() => setBatteryGranularity('hourly')}
          >
            Hourly
          </button>
        </div>
      )}

      {data && (
        <div className="mppt-stats">
          <div className="stat-item">
            <span className="stat-label">Data Coverage</span>
            <span className="stat-value">{data.daysWithData}/{data.totalDays} days</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Readings</span>
            <span className="stat-value">{data.totalReadings?.toLocaleString()}</span>
          </div>
        </div>
      )}

      <div className="chart-container">
        {loading ? (
          <div className="chart-loading">Loading MPPT data...</div>
        ) : error ? (
          <div className="chart-error">{error}</div>
        ) : (
          chartData && <Line options={getOptions()} data={chartData} />
        )}
      </div>
    </div>
  );
};

export default MPPTChart;
